import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Input, Spinner, Form } from 'reactstrap';
import Alert from '@mui/material/Alert';
import classes from './SignUpForm.module.css';
import axiosStandard from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import {ToastContainer } from 'react-toastify';
import { IoPerson, IoLockClosed, IoLocation } from 'react-icons/io5';
import { BsAt, BsHouses } from "react-icons/bs";
import { FaUsers, FaPhone } from "react-icons/fa";
import { Stack, Stepper, Step, StepLabel } from '@mui/material';
import ColorlibConnector from './Components/ColorlibConnector';
import ColorlibStepIcon from './Components/ColorlibStepIcon';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { IoMailUnreadOutline ,IoLocateOutline } from "react-icons/io5";

const steps = ['fill_data', 'confirm_email'];

function SignUpForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    nom_client: '',
    prenom_client: '',
    addresse_client: '',
    ville_client: '',
    phone_client: '',
    password_client: '',
    cp_client: '',
    province_client: '',
    email_client: ''
  });

  const [validationStatus, setValidationStatus] = useState({
    email_client: true,
    prenom_client: true,
    nom_client: true,
    phone_client: true,
    password_client: true,
    addresse_client: true,
    ville_client: true,
    cp_client: true,
  });

  const [activeStep, setActiveStep] = useState(0);
  const [cpswd, setCpswd] = useState('');
  const [isNotValidForm, setIsNotValidForm] = useState(false);

  const mutation = useMutation({
    mutationFn: (data) => {
      const API_URL = process.env.REACT_APP_APIURL
      return axiosStandard.post(`${API_URL}/clients/inscription`, data);
    },
    onSuccess: () => {
      Swal.fire({
        title: t("signup_success"),
        text: t("check_email"),
        icon: 'success',
        confirmButtonColor: '#4AAE9B',
      }).then(() => navigate('/login'));
    },
    onError: (err) => {
      const responseData = err.response?.data;
      let msg = t("signup_failed");
      if (responseData?.error && typeof responseData.error === 'object') {
        msg = Object.values(responseData.error).flat().join(' | ');
      } else if (responseData?.message) {
        msg = responseData.message;
      }
      Swal.fire({ title: t("signup_failed"), text: msg, icon: 'error', confirmButtonColor: '#4AAE9B' });
    }
  });

  const handleNextStepChange = () => {
    const isValidEmail = /\S+@\S+\.\S+/.test(userData.email_client);
    const isPasswordMatch = cpswd === userData.password_client && cpswd !== '';
    const isRequiredFilled = userData.nom_client !== '' && userData.prenom_client !== '' &&
      userData.addresse_client !== '' && userData.ville_client !== '' &&
      userData.cp_client !== '';

    setValidationStatus({
      ...validationStatus,
      email_client: isValidEmail,
      password_client: isPasswordMatch,
      prenom_client: userData.prenom_client !== '',
      nom_client: userData.nom_client !== '',
      addresse_client: userData.addresse_client !== '',
      ville_client: userData.ville_client !== '',
      cp_client: userData.cp_client !== '',
    });

    if (isValidEmail && isPasswordMatch && isRequiredFilled) {
      setActiveStep(1);
      setIsNotValidForm(false);
    } else {
      setIsNotValidForm(true);
    }
  };

  const handleSignUp = (e) => {
    if (e) e.preventDefault();
    mutation.mutate(userData);
  };

  return (
    <Row className={classes.formContainer}>
      <ToastContainer />
      <Col xs={12} md={10} className="mx-auto">
        <h4 className={classes.headerTitle}>{t("signup")}</h4>

        <Stack sx={{ width: '100%' }} className='mb-5'>
          <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
            {steps.map(label => (
              <Step key={label}><StepLabel StepIconComponent={ColorlibStepIcon}>{t(label)}</StepLabel></Step>
            ))}
          </Stepper>
        </Stack>

        {isNotValidForm && <Alert severity="error" className='mb-3'>{t("verify_info")}</Alert>}

        <Form onSubmit={(e) => e.preventDefault()}>
          {activeStep === 0 ? (
            <>
              {/* Email et Téléphone */}
              <Row>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>

                    <Input type="email" placeholder={t("email_address")}
                      onChange={e => setUserData({ ...userData, email_client: e.target.value })}
                      invalid={!validationStatus.email_client} />
                    <span className={classes.icon}><BsAt />
                                        <span className={classes.asterisk}>*</span>

                    </span>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>
                    <Input placeholder={t("phone")}
                      onChange={e => setUserData({ ...userData, phone_client: e.target.value })} />
                    <span className={classes.icon}><FaPhone />
                                        <span className={classes.asterisk}>*</span>

                    </span>
                  </div>
                </Col>
              </Row>

              {/* Prénom et Nom */}
              <Row>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>

                    <Input placeholder={t("firstname")}
                      onChange={e => setUserData({ ...userData, prenom_client: e.target.value })}
                      invalid={!validationStatus.prenom_client} />
                    <span className={classes.icon}><IoPerson />
                                        <span className={classes.asterisk}>*</span>

                    </span>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>

                    <Input placeholder={t("lastname")}
                      onChange={e => setUserData({ ...userData, nom_client: e.target.value })}
                      invalid={!validationStatus.nom_client} />
                    <span className={classes.icon}><FaUsers />
                                        <span className={classes.asterisk}>*</span>

                    </span>
                  </div>
                </Col>
              </Row>

              {/* Adresse et Ville */}
              <Row>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>

                    <Input placeholder={t("address")}
                      onChange={e => setUserData({ ...userData, addresse_client: e.target.value })}
                      invalid={!validationStatus.addresse_client} />
                    <span className={classes.icon}><BsHouses />
                      <span className={classes.asterisk}>*</span>

                    </span>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>

                    <Input placeholder={t("city")}
                      onChange={e => setUserData({ ...userData, ville_client: e.target.value })}
                      invalid={!validationStatus.ville_client} />
                    <span className={classes.icon}><IoLocation />
                      <span className={classes.asterisk}>*</span>
                    </span>
                  </div>
                </Col>
              </Row>

              {/* Code Postal et Province */}
              <Row>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>


                    <Input placeholder={t("postalCode")}
                      onChange={e => setUserData({ ...userData, cp_client: e.target.value })}
                      invalid={!validationStatus.cp_client} />
                    <span className={classes.icon}><IoMailUnreadOutline />
                      <span className={classes.asterisk}>*</span>
                    </span>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>
                    <Input placeholder={t("province")}
                      onChange={e => setUserData({ ...userData, province_client: e.target.value })} />
                    <span className={classes.icon}><IoLocateOutline /></span>
                  </div>
                </Col>
              </Row>

              {/* Mots de passe */}
              <Row>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>

                    <Input type="password" placeholder={t("password")}
                      onChange={e => setUserData({ ...userData, password_client: e.target.value })} />
                    <span className={classes.icon}><IoLockClosed />
                      <span className={classes.asterisk}>*</span>

                    </span>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className={`mb-3 ${classes.inputWithIcon}`}>

                    <Input type="password" placeholder={t("confirm_password")}
                      onChange={e => setCpswd(e.target.value)}
                      invalid={!validationStatus.password_client} />
                    <span className={classes.icon}><IoLockClosed /> <span className={classes.asterisk}>*</span>
                    </span>
                  </div>
                </Col>
              </Row>

              <Row className="mt-4 mb-4">
                <Col xs={12} md={6} className="mx-auto text-center">
                  <h4 style={{ color: "rgb(130,130,130)", fontSize: "15px" }}>
                    <Button color="success" onClick={handleNextStepChange}>{t("next")} </Button> </h4></Col>
              </Row>
            </>
          ) : (
            <Row className="text-center">
              <Col xs={12}>
                <p className="mb-4">{t("please_confirm_to_finish")}</p>
                <Button color="secondary" className="me-2" onClick={() => setActiveStep(0)}>{t("back")}</Button>
                <Button color="success" onClick={handleSignUp} disabled={mutation.isPending}>
                  {mutation.isPending ? <Spinner size="sm" /> : t("confirm_signup")}
                </Button>
              </Col>
            </Row>
          )}
        </Form>

        <Row className="mt-4 mb-4">
          <Col xs={12} md={6} className="mx-auto text-center">
            <h4 style={{ color: "rgb(130,130,130)", fontSize: "15px" }}>
              {t("already_have_account")} <NavLink to="/login" style={{ textDecoration: 'none' }}>{t("login")}</NavLink>
            </h4>
          </Col>
        </Row>

      </Col>
    </Row>
  );
}

export default SignUpForm;
